<template>
  <div class="user-information">
    <form
      action="#"
      novalidate
    >
      <div class="form-input-container">
        <ScLabel>
          <div class="mail-change-btn-wrapper">
            <span>
              {{ $t('account.labels.userEmail') }}
              <ScTooltip
                v-if="!canChangeEmail"
                class="mail-info-tooltip"
              >
                <template #tooltip-trigger>
                  <GIcon
                    name="info"
                    stroke="var(--light-grey-500)"
                    size="14px"
                    class="info-icon"
                  />
                </template>
                <template #tooltip-text>
                  <span
                    v-html="$t('account.shouldUpdatePhoneTooltip')"
                  />
                </template>
              </ScTooltip>
            </span>
            <div class="btn-wrapper">
              <span
                v-if="canChangeEmail"
                class="update-btn"
                @click="showEmailChangeModal=true"
              >
                {{ $t('account.labels.update') }}
              </span>
              <span
                v-if="canVerifyEmail"
                class="verify-btn"
                @click="verifyEmail"
              >
                {{ $t('account.labels.verify') }}
              </span>
            </div>
          </div>
        </ScLabel>
        <div
          class="input-container mail-wrapper"
          :class="{ 'verified': mailVerified === true, 'unverified': mailVerified === false }"
        >
          <ScInput
            class="form-input"
            v-model="userEmail"
            :disabled="true"
          />
          <div class="icon-wrapper">
            <GIcon
              v-if="mailVerified === true"
              name="check-circle"
              stroke="var(--green-500)"
              size="14px"
              class="completed-icon"
            />
            <GIcon
              v-else-if="mailVerified === false"
              name="alert-circle"
              stroke="var(--red-500)"
              size="14px"
              class="alert-icon"
            />
          </div>
        </div>
      </div>
      <div class="form-input-container">
        <ScLabel>
          <div class="phone-number-change-btn-wrapper">
            {{ $t('account.labels.userPhone') }}
            <span
              v-if="canChangePhoneNumber"
              class="update-btn"
              @click="showPhoneNumberChangeModal=true"
            >
              {{ $t('account.labels.update') }}
            </span>
          </div>
        </ScLabel>
        <div
          class="input-container phone-wrapper"
          :class="{ 'verified': phoneVerified === true, 'unverified': phoneVerified === false }"
        >
          <ScInput
            class="form-input"
            v-model="userPhone"
            :disabled="true"
          />
          <div class="icon-wrapper">
            <GIcon
              v-if="phoneVerified === true"
              name="check-circle"
              stroke="var(--green-500)"
              size="14px"
              class="completed-icon"
            />
            <GIcon
              v-else-if="phoneVerified === false"
              name="alert-circle"
              stroke="var(--red-500)"
              size="14px"
              class="alert-icon"
            />
          </div>
        </div>
        <span
          class="error-message"
          v-if="phoneVerified === false"
        >
          Lütfen telefon numaranızı güncelleyiniz.
        </span>
      </div>
      <div class="form-input-container password-change-btn-wrapper">
        <ScLabel>
          <div class="password-change-btn-wrapper">
            {{ $t('account.labels.myPassword') }}
            <span
              v-if="canChangePassword"
              class="inline-btn update-btn"
              @click="showPasswordChangeModal=true"
            >
              {{ $t('account.labels.update') }}
            </span>
          </div>
        </ScLabel>
        <div class="input-container password-wrapper">
          <ScInput
            class="form-input"
            value="-------"
            type="password"
            :disabled="true"
          />
        </div>
      </div>
      <div class="form-input-container invalidate-all-sessions-wrapper">
        <span
          class="inline-btn invalidate-all-sessions-btn"
          @click="showInvalidateAllSessionsModal=true"
        >
          {{ $t('account.logoutAllUsers.btnText') }}
        </span>
      </div>
    </form>
    <ChangeEmailModal
      :show="showEmailChangeModal"
      :data="{ currentEmail: userEmail }"
      @close="showEmailChangeModal=false"
      @submit="sendOtpForUpdateEmail"
    />
    <ChangePasswordModal
      :show="showPasswordChangeModal"
      :data="{}"
      @close="showPasswordChangeModal=false"
      @submit="onChangePassword"
    />
    <ChangePhoneNumberModal
      :show="showPhoneNumberChangeModal"
      :data="{}"
      @close="showPhoneNumberChangeModal=false"
      @submit="sendOtpForUpdatePhone"
    />
    <InvalidateAllSessionsModal
      :show="showInvalidateAllSessionsModal"
      @close="showInvalidateAllSessionsModal=false"
      @submit="onInvalidateAllSessions"
    />
    <OtpModal
      v-if="otpModal.visibility"
      :otp-remaining-time-object="otpModal.otpRemainingTime"
      @submit="validateOtp"
      @resend="sendOtp"
      @close="otpModal.visibility = false;"
    />
    <OtpErrorModal
      v-else-if="otpModal.otpBlocked"
      @close="onOtpErrorModalClose"
    />
  </div>
</template>

<script>
import ScInput from '@trendyol/sc-ui-kit/ScInput';
import ScLabel from '@trendyol/sc-ui-kit/ScLabel';
import ScTooltip from '@trendyol/sc-ui-kit/ScTooltip';
import ChangePasswordModal from '@/components/Authorization/ChangePasswordModal';
import ChangePhoneNumberModal from '@/components/Authorization/ChangePhoneNumberModal';
import ChangeEmailModal from '@/components/Info/Modals/ChangeEmailModal';
import SupplierService from '@/services/SupplierService';
import TokenService from '@/services/TokenService';
import account from '@/services/account';
import OtpModal from '@/components/Shared/OtpModal';
import OtpErrorModal from '@/components/Shared/OtpErrorModal';
import { remoteConfig } from '@/services';
import { SellerCenterAccountApiClient } from '@/apis';
import InvalidateAllSessionsModal from '@/components/Info/Modals/InvalidateAllSessionsModal';
import GIcon from '@trendyol-js/grace/core/GIcon';

export default {
  name: 'UserInformation',
  components: {
    InvalidateAllSessionsModal,
    ScInput,
    ScLabel,
    ScTooltip,
    ChangePasswordModal,
    ChangePhoneNumberModal,
    OtpModal,
    OtpErrorModal,
    ChangeEmailModal,
    GIcon,
  },
  props: {
    editable: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      otpModal: {
        type: null,
        visibility: false,
        otpRemainingTime: {},
        otpBlocked: false,
      },
      changeEmailData: {},
      captchaPayload: '',
      showPhoneNumberChangeModal: false,
      showInvalidateAllSessionsModal: false,
      showPasswordChangeModal: false,
      userEmail: '',
      userPhone: '',
      phoneVerified: null,
      phoneNumber: '',
      showEmailChangeModal: false,
      mailVerified: null,
      oldEmail: '',
      newEmail: '',
    };
  },
  computed: {
    canChangePassword () {
      return !SupplierService.comeFromAdminPanel() && this.editable;
    },
    canChangePhoneNumber () {
      return remoteConfig.getConfigValueByKey('SHOW_UPDATE_PHONE') && this.editable;
    },
    canChangeEmail () {
      return this.editable && this.phoneVerified;
    },
    canVerifyEmail () {
      return this.editable && this.phoneVerified && !this.mailVerified;
    },
  },
  methods: {
    async onInvalidateAllSessions () {
      await SellerCenterAccountApiClient.post('/token/invalidate-all', {
        refreshToken: TokenService.getRefreshToken(),
      });
      this.$gtm.trackEvent({
        event: 'GAEvent',
        eventCategory: 'Onboarding - Hesabım',
        eventAction: 'Tüm Oturumları Kapat',
        eventLabel: SupplierService.getSupplierId(),
      });

      this.$toast.success('Oturumları kapatma talebiniz işleme alınmıştır.');
      this.showInvalidateAllSessionsModal = false;
    },
    sendOtp (captchaPayload, data) {
      if (this.otpModal.type === 'updatePhone') {
        this.sendOtpForUpdatePhone(captchaPayload, data);
      } else if (this.otpModal.type === 'updateEmail') {
        this.sendOtpForUpdateEmail(captchaPayload, data);
      }
    },
    validateOtp (code) {
      if (this.otpModal.type === 'updatePhone') {
        this.validateOtpForUpdatePhone(code);
      } else if (this.otpModal.type === 'updateEmail') {
        this.validateOtpForUpdateEmail(code);
      }
    },
    onOtpErrorModalClose () {
      this.otpModal.otpBlocked = false;
      this.captchaPayload = '';
    },
    async verifyEmail (showToast = true) {
      await account.verifyEmail();
      if (showToast) { this.$toast.success('Doğrulama maili e-posta adresinize gönderildi.'); }
    },
    async sendOtpForUpdatePhone (captchaPayload, { phoneNumber }) {
      this.otpModal.type = 'updatePhone';
      this.captchaPayload = captchaPayload;
      const phone = phoneNumber;

      this.phoneNumber = phone;
      const options = {
        headers: {
          'x-g-invisible-recaptcha': captchaPayload,
        },
      };

      try {
        const res = await account.resetPhone({
          phone,
        }, options);

        if (res) {
          this.otpModal.otpRemainingTime = res.remainingForOperation || {};
          this.otpModal.visibility = true;
        }
      } catch (err) {
        if (err.response.status === 429) {
          this.otpModal.otpBlocked = true;

          return true;
        }
      }
    },
    async validateOtpForUpdatePhone (code) {
      const res = await account.resetPhoneValidateOtp({
        phoneNumber: this.phoneNumber,
        code,
      });
      if (res.reason === 'SUCCESS') {
        this.captchaPayload = '';
        this.otpModal.visibility = false;
        await account.updateUserPhone({
          phone: this.phoneNumber,
          activeSessionToken: TokenService.getToken('refresh_token'),
        });
        await this.getUserInformation();
        this.phoneNumber = null;
        this.showPhoneNumberChangeModal = false;
        this.$toast.success('Telefon numaranız başarıyla güncellendi.');

        this.$gtm.trackEvent({
          event: 'GAEvent',
          eventCategory: 'Onboarding - Hesabım',
          eventAction: 'Telefon Numaramı Güncelle',
          eventLabel: SupplierService.getSupplierId(),
        });
      } else {
        this.$toast.error('Lütfen kodu kontrol edip tekrar giriniz.');
      }
    },
    async sendOtpForUpdateEmail (captchaPayload, changeEmailData) {
      this.otpModal.type = 'updateEmail';
      this.captchaPayload = captchaPayload;
      this.changeEmailData = changeEmailData;

      const options = {
        headers: {
          'x-g-invisible-recaptcha': captchaPayload,
        },
      };

      try {
        const res = await account.sendOtpUpdateEmail({
          phone: this.userPhone,
          email: this.changeEmailData.newEmail,
        }, options);

        if (res) {
          this.otpModal.otpRemainingTime = res.remainingForOperation || {};
          this.otpModal.visibility = true;
        }
      } catch (err) {
        if (err.response.status === 429) {
          this.otpModal.otpBlocked = true;

          return true;
        }
      }
    },
    async validateOtpForUpdateEmail (code) {
      try {
        await account.validateOtpUpdateEmail({
          phone: this.userPhone,
          code,
          email: this.changeEmailData.currentEmail,
          newEmail: this.changeEmailData.newEmail,
          activeSessionToken: TokenService.getToken('refresh_token'),
        });
        this.captchaPayload = '';
        this.otpModal.visibility = false;
        this.changeEmailData = {};
        this.showEmailChangeModal = false;
        await this.getUserInformation();
        this.$toast.success('E-posta adresiniz başarıyla güncellendi.');
        window.scEventBus.publish('LOGOUT');

        this.$gtm.trackEvent({
          event: 'GAEvent',
          eventCategory: 'Onboarding - Hesabım',
          eventAction: 'Email Güncelle',
          eventLabel: SupplierService.getSupplierId(),
        });
      } catch (err) {
        if (err.response && err.response.data && Array.isArray(err.response.data.errors) && err.response.data.errors.every(error => !error.key.startsWith('sms.api.client.error'))) {
          this.otpModal.visibility = false;
        }

        throw err;
      }
    },
    async onChangePassword ({ data }) {
      let formattedData = data.reduce((obj, item) => {
        obj[item.key] = item.value;
        return obj;
      }, {});
      delete formattedData.passwordConfirmation;
      formattedData.token = TokenService.getToken('refresh_token');
      await account.changePassword(formattedData);
      this.$gtm.trackEvent({
        event: 'GAEvent',
        eventCategory: 'Onboarding - Hesabım',
        eventAction: 'Şifremi Güncelle',
        eventLabel: SupplierService.getSupplierId(),
      });
      this.$toast.success('Şifreniz güncellendi.', {
        duration: 5000,
      });
      this.showPasswordChangeModal = false;
    },
    async getUserInformation () {
      const userInformation = await account.getUserInformation();

      this.userEmail = userInformation.email;
      this.userPhone = userInformation.phone || '---';
      this.phoneVerified = userInformation.phoneVerified;
      this.mailVerified = userInformation.mailVerified;
    },
  },
  created () {
    this.showPhoneNumberChangeModal = !!this.$route.params.phoneVerificationRequired;

    this.getUserInformation();
  },
};
</script>
<style scoped lang="scss">
  .user-information{
    form{
      .invalidate-all-sessions-wrapper {
        margin-top: 30px;
      }

      .form-input-container {
        width: 33%;

        .phone-number-change-btn-wrapper, .mail-change-btn-wrapper, .password-change-btn-wrapper{
          display: flex;
          justify-content: space-between;

          .btn-wrapper{
            display: flex;
            align-items: center;
          }

          .mail-info-tooltip {
            & .info-icon {
              cursor: pointer;
            }
            &::v-deep .tooltip-text{
              width: 300px;
            }
          }
        }

        .name-label-wrapper {
          display: flex;
          justify-content: space-between;
        }

        .inline-btn {
          font-weight: 500;
          font-size: 12px;
          color: #f27a1a;
          cursor: pointer;
          text-decoration: underline;
        }

        .invalidate-all-sessions-btn {
          color: #ff5043;
        }

        .verify-btn {
          font-weight: 500;
          font-size: 12px;
          color: #f27a1a;
          cursor: pointer;
          text-decoration: underline;
          margin-left: 16px;
        }

        &:not(:last-child){
          margin-bottom: 16px;
        }

        ::v-deep .mp-label{
          font-size: 14px;
          .icon{
            margin-right: 0;
          }
        }

        .input-container{
          width: 100%;
          margin-top: 10px;
          display: flex;
          align-items: center;
          position: relative;

          .icon-wrapper {
            position: absolute;
            right: 10px;
          }

          .form-input{
            width: 100%;
          }

          &.phone-wrapper, &.mail-wrapper {
            &.unverified {
              ::v-deep .form-input input {
                border-color: #ff5043;
                background-color: rgba(255, 80, 67, 0.1);
              }
            }

            &.verified {
              ::v-deep .form-input input {
                border-color: #0bc15c;
                background-color: #e6f8ee;
              }
            }
          }
        }

        .error-message {
          color: #ff5043;
          margin-left: 15px;
          margin-top: 5px;
          display: block;
        }
      }
      .button-container{
        display: flex;
        align-items: flex-end;
        .save-btn{
          margin-left: auto;
        }
      }
    }

    @media only screen and (max-width: 992px) {
      form .form-input-container {
        width: 100%;
      }
    }

    .update-btn {
      font-weight: 500;
      font-size: 12px;
      color: #f27a1a;
      cursor: pointer;
      text-decoration: underline;
    }
  }

</style>
